import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowRightLeft, Check, Search, Loader2, FileSpreadsheet, ChevronLeft } from "lucide-react";

const fmtEur = (n) => `${Number(n || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fmtNum = (n) => Number(n || 0).toLocaleString("es-ES");

export default function TrasladoSimplePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({}); // {gh: {lab, units}}
  const [search, setSearch] = useState("");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/orders/transfer-candidates");
        setData(r.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Error cargando candidatos");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!data?.candidates) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.candidates;
    return data.candidates.filter((c) => c.gh.toLowerCase().includes(q));
  }, [data, search]);

  const toggleRow = (gh, suggested, defaultUnits) => {
    setSelected((s) => {
      const next = { ...s };
      if (next[gh]) delete next[gh];
      else next[gh] = { lab: suggested, units: defaultUnits };
      return next;
    });
  };

  const setRowLab = (gh, lab) => setSelected((s) => ({ ...s, [gh]: { ...s[gh], lab } }));
  const setRowUnits = (gh, units) => setSelected((s) => ({ ...s, [gh]: { ...s[gh], units: Math.max(0, parseInt(units || 0)) } }));

  const selectAllFiltered = () => {
    const additions = {};
    filtered.forEach((c) => {
      if (!selected[c.gh]) additions[c.gh] = { lab: c.suggested_lab, units: c.units_to_transfer };
    });
    setSelected((s) => ({ ...s, ...additions }));
  };

  const clearAll = () => setSelected({});

  const exportXlsx = async () => {
    const lines = Object.entries(selected).map(([gh, v]) => ({ gh, lab: v.lab, units: v.units }));
    if (!lines.length) {
      toast.error("Selecciona al menos una molécula");
      return;
    }
    setExporting(true);
    try {
      const r = await api.post("/orders/transfer-simple/export", { lines }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "traslado_simple.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Excel generado · ${lines.length} líneas`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error en la exportación");
    } finally {
      setExporting(false);
    }
  };

  const selCount = Object.keys(selected).length;
  const selUnits = Object.values(selected).reduce((s, x) => s + (x.units || 0), 0);

  if (loading) return <div className="p-6 text-slate-400 inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Cargando moléculas…</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1500px]" data-testid="transfer-simple-page">
      <Link to="/proveedores" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-4">
        <ChevronLeft className="h-4 w-4" /> Volver a proveedores
      </Link>

      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2 inline-flex items-center gap-2">
            <ArrowRightLeft className="h-3.5 w-3.5" /> Traslado simple · {data?.dispensation?.label}
          </div>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tighter text-slate-950">
            Elige moléculas y proveedor a tu medida
          </h1>
          <p className="text-slate-500 mt-2 max-w-3xl text-sm">
            Lista plana de TODAS las moléculas con dispensación de marca. Marca las que quieras (1 a 1 o de 5 en 5) y elige libremente a qué laboratorio EFG las trasladas — sin restricciones de condiciones.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={exportXlsx}
            disabled={exporting || selCount === 0}
            data-testid="export-transfer-xlsx"
            className="inline-flex items-center gap-1.5 bg-emerald-600 text-white px-4 py-2 rounded-md text-sm font-bold hover:bg-emerald-700 disabled:opacity-40"
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
            Generar Excel ({selCount})
          </button>
        </div>
      </header>

      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4 flex flex-wrap items-center gap-3" data-testid="transfer-controls">
        <div className="flex items-center gap-2 flex-1 min-w-[240px]">
          <Search className="h-4 w-4 text-slate-500" />
          <input
            data-testid="transfer-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar molécula (ej. 'amlodipino', 'estatina')…"
            className="flex-1 px-3 py-1.5 border border-slate-200 rounded-md text-sm"
          />
        </div>
        <button onClick={selectAllFiltered} className="text-xs font-semibold px-3 py-1.5 rounded-md bg-slate-100 hover:bg-slate-200">
          + Seleccionar todas las visibles ({filtered.length})
        </button>
        <button onClick={clearAll} disabled={selCount === 0} className="text-xs font-semibold px-3 py-1.5 rounded-md bg-slate-100 hover:bg-slate-200 disabled:opacity-40">
          × Limpiar
        </button>
        <div className="text-xs font-mono-num text-slate-600 ml-auto">
          <strong className="text-slate-900">{selCount}</strong> seleccionadas · <strong className="text-slate-900">{selUnits}</strong> uds
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="p-2 w-10"></th>
              <th className="text-left p-2 font-semibold">Molécula (G.H.)</th>
              <th className="text-right p-2 font-semibold">Marca uds/mes</th>
              <th className="text-right p-2 font-semibold">€ marca/mes</th>
              <th className="text-right p-2 font-semibold w-24">Uds a pedir</th>
              <th className="text-left p-2 font-semibold w-[260px]">Laboratorio destino</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map((c) => {
              const isSel = !!selected[c.gh];
              const cur = selected[c.gh] || {};
              return (
                <tr key={c.gh} className={isSel ? "bg-emerald-50/50" : "hover:bg-slate-50"} data-testid={`row-${c.gh.slice(0,30)}`}>
                  <td className="p-2 text-center">
                    <input
                      type="checkbox"
                      checked={isSel}
                      onChange={() => toggleRow(c.gh, c.suggested_lab, c.units_to_transfer)}
                      className="h-4 w-4 rounded border-slate-300 text-emerald-600"
                      data-testid={`check-${c.gh.slice(0,20)}`}
                    />
                  </td>
                  <td className="p-2 text-sm text-slate-900 truncate max-w-[420px]" title={c.gh}>{c.gh}</td>
                  <td className="p-2 text-right font-mono-num text-slate-600">{fmtNum(c.marca_units_pm)}</td>
                  <td className="p-2 text-right font-mono-num font-semibold">{fmtEur(c.marca_eur_pm)}</td>
                  <td className="p-2 text-right">
                    {isSel ? (
                      <input
                        type="number"
                        min={0}
                        value={cur.units}
                        onChange={(e) => setRowUnits(c.gh, e.target.value)}
                        className="w-20 px-2 py-1 text-right border border-slate-200 rounded text-sm font-mono-num"
                      />
                    ) : (
                      <span className="text-xs font-mono-num text-slate-400">{c.units_to_transfer}</span>
                    )}
                  </td>
                  <td className="p-2">
                    {isSel ? (
                      <select
                        value={cur.lab}
                        onChange={(e) => setRowLab(c.gh, e.target.value)}
                        className="w-full px-2 py-1 border border-slate-300 rounded text-xs bg-white"
                      >
                        {c.candidate_labs.map((l) => (
                          <option key={l.lab} value={l.lab}>
                            {l.lab} {l.has_agreement ? "✓" : ""} (PVL {l.pvl}€{l.stock_units > 0 ? `, ${l.stock_units}u stock` : ""})
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-xs text-slate-400 inline-flex items-center gap-1">
                        sugerido: <strong className="text-slate-700">{c.suggested_lab}</strong>
                        {c.candidate_labs.find((l) => l.lab === c.suggested_lab && l.has_agreement) && <Check className="h-3 w-3 text-emerald-600" />}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-slate-400">Sin resultados para “{search}”.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
